import { useEffect } from 'react';
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { usePwaUpdate } from '@/hooks/usePwaUpdate';
import { SidebarProvider } from '@/contexts/sidebar-context';
import { ThemeProvider } from '@/contexts/theme-context';
import { useI18n } from '@/contexts/i18n-hook';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { ProjetoRoute } from '@/components/auth/ProjetoRoute';
import { Layout } from '@/components/layout/layout';
import { Login } from '@/pages/auth/login/login';
import { ProjetoSelecao } from '@/pages/projeto-selecao/projeto-selecao';
import { Dashboard } from '@/pages/dashboard/dashboard';
import { Estatisticas } from '@/pages/estatisticas/estatisticas';
import { ApreciacoesList } from '@/pages/apreciacoes/apreciacoes-list';
import { ApreciacaoCreate } from '@/pages/apreciacoes/apreciacao-create';
import { ApreciacaoEdit } from '@/pages/apreciacoes/apreciacao-edit';
import { ApreciacaoEditV2 } from '@/pages/apreciacoes/apreciacao-edit-v2';
import { ApreciacaoView } from '@/pages/apreciacoes/apreciacao-view';
import { MaquinasList } from '@/pages/maquinas/maquinas-list';
import { MaquinaCreate } from '@/pages/maquinas/maquina-create';
import { MaquinaDetalhe } from '@/pages/maquinas/maquina-detalhe';
import { MaquinaEdit } from '@/pages/maquinas/maquina-edit';
import { TemplatesList } from '@/pages/templates/templates-list';
import { TemplateCreate } from '@/pages/templates/template-create';
import { TemplateEdit } from '@/pages/templates/template-edit';
import { RevisoesList } from '@/pages/revisoes/revisoes-list';
import { RevisaoDetalhePage } from '@/pages/revisoes/revisao-detalhe';
import { TemplateMultiParteEdit } from '@/pages/templates/template-multiparte-edit';
import { TemplateParteEdit } from '@/pages/templates/template-parte-edit';
import { ModelosPage } from '@/pages/templates/modelos/modelos-page';
import { AdminUsuariosList } from '@/pages/admin/AdminUsuariosList';
import { AdminUsuarioForm } from '@/pages/admin/AdminUsuarioForm';
import { AdminFeedbacksList } from '@/pages/admin/AdminFeedbacksList';
import { AdminSquadsPage } from '@/pages/admin/AdminSquadsPage';
import { SquadOverviewPage } from '@/pages/squads/SquadOverviewPage';
import { EstruturacaoRoute } from '@/pages/squads/EstruturacaoRoute';
import { SquadAvaliacaoPage } from '@/pages/squads/SquadAvaliacaoPage';
import { BudgetPage } from '@/pages/budget/BudgetPage';
import { CarteiraPage } from '@/pages/carteira/CarteiraPage';
import { CarteiraRoute } from '@/pages/carteira/CarteiraRoute';
import { EquipeCarteiraPage } from '@/pages/carteira/EquipeCarteiraPage';
import { CotacoesListPage } from '@/pages/vendas/CotacoesListPage';
import { VendasRoute } from '@/pages/vendas/VendasRoute';
import { CotacaoWorkspacePage } from '@/pages/vendas/CotacaoWorkspacePage';
import { PerfilPage } from '@/pages/perfil/PerfilPage';
import { ModecMergePage } from '@/pages/modec/modec-merge-page';
import { ManualOperacaoPage } from '@/pages/manual-operacao/manual-operacao-page';
import { ManuaisListPage } from '@/pages/manual-operacao/manuais-list';
import { RelatorioMensal } from '@/pages/relatorio/relatorio-mensal';
import { CronogramaPage } from '@/pages/cronograma/cronograma-page';
import { CapturePage } from '@/features/photo-capture/capture-page';
import { ImagensMaquinasPage } from '@/pages/imagens-maquinas/imagens-maquinas-page';
import { IAPage } from '@/pages/ia/ia-page';
import { IARoute } from '@/pages/ia/IARoute';
import { PortalDashboard } from '@/pages/portal/portal-dashboard';
import { PortalApreciacoesList } from '@/pages/portal/portal-apreciacoes-list';
import { PortalApreciacaoReview } from '@/pages/portal/portal-apreciacao-review';
import { PortalLayout } from '@/components/portal/PortalLayout';
import { useAuth } from '@/contexts/auth-hook';
import { useProjeto } from '@/contexts/projeto-hook';
import { hasStoredProject, resolveAuthenticatedEntryRoute, saveLastAppRoute } from '@/lib/session-utils';

function RoutePersistence() {
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) return;
    const route = `${location.pathname}${location.search}${location.hash}`;
    saveLastAppRoute(route);
  }, [isAuthenticated, location.pathname, location.search, location.hash]);

  return null;
}

function RootEntry() {
  const { isAuthenticated, loading: authLoading, isAdmin, user } = useAuth();
  const { projeto, loading: projetoLoading } = useProjeto();
  const { t } = useI18n();

  if (authLoading || (isAuthenticated && user?.role !== 'cliente' && projetoLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-schmersal-gray-light dark:bg-schmersal-gray-darkest">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-schmersal-blue mx-auto mb-4"></div>
          <p className="text-schmersal-gray-medium dark:text-schmersal-gray-light">
            {t('layout.app.carregando')}
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Clientes vao direto para o portal
  if (user?.role === 'cliente') {
    return <Navigate to="/portal" replace />;
  }

  const projectAvailable = Boolean(projeto) || hasStoredProject();
  const entryRoute = resolveAuthenticatedEntryRoute(projectAvailable, isAdmin);
  // Projeto de manual entra pelo histórico: é dele que se abre, copia ou cria
  // um documento, e não mais direto num editor sem manual selecionado.
  // Só para admin — o módulo ainda está restrito.
  const projectEntryRoute = entryRoute === '/dashboard' && projeto?.tipoProjeto === 'manual_operacao' && isAdmin
    ? '/manuais'
    : entryRoute;
  return <Navigate to={projectEntryRoute} replace />;
}

function PwaUpdateBanner() {
  const { updateAvailable, applyUpdate } = usePwaUpdate();
  const { t } = useI18n();
  if (!updateAvailable) return null;
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 flex items-center justify-between gap-4 bg-schmersal-blue text-white px-4 py-3 shadow-lg">
      <div className="flex items-center gap-2 text-sm">
        <span className="text-lg">🔄</span>
        <span>{t('layout.app.pwaAtualizacao')}</span>
      </div>
      <button
        onClick={applyUpdate}
        className="flex-shrink-0 px-4 py-1.5 bg-white text-schmersal-blue rounded font-semibold text-sm hover:bg-schmersal-gray-light transition-colors"
      >
        {t('layout.app.pwaAtualizarAgora')}
      </button>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <PwaUpdateBanner />
      <RoutePersistence />
      <Routes>
        {/* Entrada */}
        <Route path="/" element={<RootEntry />} />

        {/* Única rota pública - Login */}
        <Route path="/login" element={<Login />} />

        {/* Portal do Cliente */}
        <Route path="/portal/login" element={<Navigate to="/login" replace />} />
        <Route
          path="/portal/*"
          element={
            <ProtectedRoute requireRole="cliente">
              <PortalLayout>
                <Routes>
                  <Route index element={<PortalDashboard />} />
                  <Route path="apreciacoes" element={<PortalApreciacoesList />} />
                  <Route path="apreciacoes/:id" element={<PortalApreciacaoReview />} />
                  <Route path="*" element={<Navigate to="/portal" replace />} />
                </Routes>
              </PortalLayout>
            </ProtectedRoute>
          }
        />

        {/* Rotas protegidas */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <SidebarProvider>
                <Routes>
                  {/* Área global: seleção de projetos e usuários (sem projeto obrigatório) */}
                  <Route path="/projeto-selecao" element={<Layout><ProjetoSelecao /></Layout>} />
                  <Route path="/perfil" element={<Layout><PerfilPage /></Layout>} />

                  {/* Estruturação de squads: atravessa projetos, então fica fora do ProjetoRoute.
                      Líder preenche a avaliação; gestão acompanha pelo overview.
                      Nem todo squad alcança — ver estruturacao-acesso. */}
                  <Route path="/squads/overview" element={<EstruturacaoRoute><Layout><SquadOverviewPage /></Layout></EstruturacaoRoute>} />
                  <Route path="/squads/avaliacao" element={<EstruturacaoRoute><Layout><SquadAvaliacaoPage /></Layout></EstruturacaoRoute>} />

                  {/* Carteira de PIs: o retrato é da empresa inteira e se filtra por
                      squad na própria tela, então não depende de projeto selecionado.
                      Nem todo squad alcança — quem não pode volta para o dashboard
                      em vez de bater num 403 vindo da API. */}
                  <Route path="/carteira" element={<CarteiraRoute><Layout><CarteiraPage /></Layout></CarteiraRoute>} />
                  {/* Equipe/custo é só de admin: o rateio parte do custo por pessoa. */}
                  <Route
                    path="/carteira/equipe"
                    element={<ProtectedRoute requireAdmin><Layout><EquipeCarteiraPage /></Layout></ProtectedRoute>}
                  />

                  {/* Vendas: a cotação nasce antes de existir projeto para o cliente, então
                      vive fora do ProjetoRoute. O escopo é por squad, feito no backend. */}
                  <Route path="/vendas/cotacoes" element={<VendasRoute><Layout><CotacoesListPage /></Layout></VendasRoute>} />
                  <Route path="/vendas/cotacoes/:id" element={<VendasRoute><Layout><CotacaoWorkspacePage /></Layout></VendasRoute>} />

                  {/* Workspace editorial dedicado, propositalmente sem menu lateral.
                      Restrito a admin enquanto o módulo está em avaliação. */}
                  <Route path="/manuais" element={<ProtectedRoute requireAdmin><ProjetoRoute><ManuaisListPage /></ProjetoRoute></ProtectedRoute>} />
                  <Route path="/manual-operacao" element={<ProtectedRoute requireAdmin><ProjetoRoute><ManualOperacaoPage /></ProjetoRoute></ProtectedRoute>} />

                  {/* Usuários (admin): mesma área que projeto-seleção, link próprio */}
                  <Route path="/admin" element={<ProtectedRoute requireAdmin><Layout><Outlet /></Layout></ProtectedRoute>}>
                    <Route index element={<Navigate to="/admin/usuarios" replace />} />
                    <Route path="usuarios" element={<AdminUsuariosList />} />
                    <Route path="usuarios/novo" element={<AdminUsuarioForm />} />
                    <Route path="usuarios/:id/editar" element={<AdminUsuarioForm />} />
                    <Route path="squads" element={<AdminSquadsPage />} />
                    <Route path="feedbacks" element={<AdminFeedbacksList />} />
                  </Route>

                  {/* Rotas que requerem projeto selecionado */}
                  <Route
                    path="/*"
                    element={
                      <ProjetoRoute>
                        <Layout>
                          <Routes>
                            {/* Dashboard */}
                            <Route path="/dashboard" element={<Dashboard />} />
                            <Route path="/" element={<Navigate to="/dashboard" replace />} />

                            {/* Estatísticas */}
                            <Route path="/estatisticas" element={<Estatisticas />} />

                            {/* Apreciações */}
                            <Route path="/apreciacoes" element={<ApreciacoesList />} />
                            <Route path="/apreciacoes/nova" element={<ApreciacaoCreate />} />
                            <Route path="/apreciacoes/:id" element={<ApreciacaoView />} />
                            <Route path="/apreciacoes/:id/editar" element={<ApreciacaoEdit />} />
                            {/* Rota oculta experimental com IA — sem link no menu */}
                            <Route path="/apreciacoes/:id/editarV2" element={<ApreciacaoEditV2 />} />

                            {/* Revisões: retorno comentado do cliente */}
                            <Route path="/revisoes" element={<RevisoesList />} />
                            <Route path="/revisoes/:id" element={<RevisaoDetalhePage />} />

                            {/* Máquinas */}
                            <Route path="/maquinas" element={<MaquinasList />} />
                            <Route path="/maquinas/nova" element={<MaquinaCreate />} />
                            <Route path="/maquinas/:id/editar" element={<MaquinaEdit />} />
                            {/* Ficha da máquina: dados, acervo documental e apreciações.
                                Vem depois de "/maquinas/nova" só por leitura — o router
                                v6 já prefere a rota literal à paramétrica. */}
                            <Route path="/maquinas/:id" element={<MaquinaDetalhe />} />

                            {/* Templates */}
                            <Route path="/meus-templates" element={<TemplatesList />} />
                            <Route path="/meus-templates/novo" element={<TemplateCreate />} />
                            <Route path="/meus-templates/multiparte/:id/editar" element={<TemplateMultiParteEdit />} />
                            <Route path="/meus-templates/parte/:id/editar" element={<TemplateParteEdit />} />
                            <Route path="/meus-templates/:id/editar" element={<TemplateEdit />} />
                            <Route path="/meus-templates/modelos" element={<ModelosPage />} />

                            {/* Modec Especiais */}
                            <Route path="/modec" element={<ModecMergePage />} />

                            {/* Cronograma e Campo */}
                            <Route path="/cronograma" element={<CronogramaPage />} />
                            <Route path="/aquisicao-campo" element={<CapturePage />} />
                            <Route path="/imagens-maquinas" element={<ImagensMaquinasPage />} />
                            <Route path="/relatorio" element={<RelatorioMensal />} />
                            <Route path="/budget" element={<BudgetPage />} />
                            {/* Assistente de IA: nem todo squad alcança — ver ia-acesso. */}
                            <Route path="/ia" element={<IARoute><IAPage /></IARoute>} />

                            {/* 404 - Redirect to Dashboard */}
                            <Route path="*" element={<Navigate to="/dashboard" replace />} />
                          </Routes>
                        </Layout>
                      </ProjetoRoute>
                    }
                  />
                </Routes>
              </SidebarProvider>
            </ProtectedRoute>
          }
        />
      </Routes>
    </ThemeProvider>
  );
}

export default App;
